import type { PendingUserQuestion, UserQuestionItem, UserQuestionOption } from '@/entities/chat/model/types';

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

function normalizeOption(value: unknown): UserQuestionOption | null {
  if (typeof value === 'string' && value.trim()) {
    const label = value.trim();
    return { label, description: label, preview: null };
  }
  if (!isRecord(value)) return null;
  const label = typeof value.label === 'string' ? value.label.trim() : '';
  const description = typeof value.description === 'string' ? value.description.trim() : '';
  if (!label || !description) return null;
  const selectionValue = typeof value.value === 'string' && value.value.trim() ? value.value.trim() : undefined;
  return {
    label,
    description,
    ...(selectionValue ? { value: selectionValue } : {}),
    preview: typeof value.preview === 'string' && value.preview.trim() ? value.preview.trim() : null,
  };
}

function normalizeQuestionItem(value: unknown): UserQuestionItem | null {
  if (!isRecord(value)) return null;
  const question = typeof value.question === 'string' ? value.question.trim() : '';
  const header = typeof value.header === 'string' ? value.header.trim() : '';
  const options = Array.isArray(value.options)
    ? value.options.map(normalizeOption).filter((option): option is UserQuestionOption => option !== null)
    : [];
  if (!question || !header) return null;
  return {
    question,
    header,
    options,
    multiSelect: value.multiSelect === true,
  };
}

function readOptionalString(payload: Record<string, unknown>, key: string): string | undefined {
  const value = payload[key];
  return typeof value === 'string' && value.trim().length > 0 ? value.trim() : undefined;
}

export function normalizeUserQuestionPayload(payload: Record<string, unknown>): PendingUserQuestion {
  const questions = normalizeQuestionList(payload.questions);
  const kind = payload.kind === 'approval'
    || payload.type === 'approval'
    || payload.type === 'approval_requested'
    ? 'approval'
    : 'user_question';
  const approvalPrompt = typeof payload.approval_prompt === 'string' && payload.approval_prompt.trim()
    ? payload.approval_prompt.trim()
    : null;
  const flatQuestion = typeof payload.question === 'string' && payload.question.trim()
    ? payload.question.trim()
    : approvalPrompt ?? questions[0]?.question ?? 'I need your input before I can continue.';
  const flatOptions = Array.isArray(payload.options)
    ? payload.options.filter((option): option is string => typeof option === 'string' && option.trim().length > 0)
    : questions[0]?.options.map((option) => option.label) ?? [];
  return {
    kind,
    id: typeof payload.id === 'string' ? payload.id : undefined,
    status: typeof payload.status === 'string' ? payload.status : undefined,
    requestId: typeof payload.request_id === 'string' ? payload.request_id : null,
    toolCallId: typeof payload.tool_call_id === 'string' ? payload.tool_call_id : null,
    question: flatQuestion,
    options: flatOptions,
    questions,
    approvalPrompt,
    ...(readOptionalString(payload, 'tool_name') !== undefined ? { toolName: readOptionalString(payload, 'tool_name') ?? null } : {}),
    ...(readOptionalString(payload, 'submit_label') !== undefined ? { submitLabel: readOptionalString(payload, 'submit_label') } : {}),
    ...(readOptionalString(payload, 'dismiss_label') !== undefined ? { dismissLabel: readOptionalString(payload, 'dismiss_label') } : {}),
    ...(readOptionalString(payload, 'details') !== undefined ? { details: readOptionalString(payload, 'details') ?? null } : {}),
    answerPending: false,
  };
}

function normalizeQuestionList(value: unknown): UserQuestionItem[] {
  if (!Array.isArray(value)) return [];
  const questions: UserQuestionItem[] = [];
  for (const item of value) {
    const normalized = normalizeQuestionItem(item);
    if (normalized) {
      questions.push(normalized);
      continue;
    }
    if (isRecord(item) && Array.isArray(item.questions)) {
      questions.push(...normalizeQuestionList(item.questions));
    }
  }
  return questions;
}
